import { Stack, styled, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isBrowser } from 'react-device-detect';

import { ROUTES } from '../../constants/routes';

import Logo from '../../assets/icons/logo.svg?react';
import authBgImage from '../../assets/images/auth-bg-map.svg';
import { SupportWidget } from '../../components/features/SupportWidget/SupportWidget';
import { usePinnedToVisualViewport } from '../../hooks/usePinnedToVisualViewport';

export const AuthorizationLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // The visual viewport shrinks when the mobile keyboard opens; sizing the
  // column to it (instead of the full layout-viewport `100%`) keeps the
  // bottom-anchored form above the keyboard rather than letting the browser
  // scroll its top — the OTP inputs — off-screen. Always pinned: this screen
  // never scrolls as a document, and any genuine overflow goes to the inner
  // `overflowY: auto` column below.
  const viewportHeight = usePinnedToVisualViewport(true);

  const hideImages = pathname === `/${ROUTES.TERMS_OF_USAGE}`;

  const StyledImg = styled('img')(() => ({
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: !isBrowser ? '100%' : '400px',
    zIndex: 0,
  }));

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#353434',
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <Stack
        sx={(theme: Theme) => ({
          maxWidth: !isBrowser ? '100%' : '400px',
          width: '100%',
          margin: '0 auto',
          position: 'relative',
          height: viewportHeight ? `${viewportHeight}px` : '100%',
          backgroundColor: theme.palette.gray[300],
          overflow: 'hidden',
        })}
      >
        <Logo
          style={{
            position: 'absolute',
            top: '15px',
            left: '22px',
            zIndex: 1,
          }}
        />
        <Stack
          sx={{
            position: 'absolute',
            top: '15px',
            right: '22px',
            zIndex: 1,
          }}
        >
          <SupportWidget />
        </Stack>
        {!hideImages ? (
          <StyledImg src={authBgImage} alt={t('userAuth.bgImageAlt')} />
        ) : null}
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          {/* margin-top:auto bottom-anchors the form when there's spare space,
              but lets it scroll (top reachable) once the keyboard makes the
              content taller than the visible viewport. */}
          <Stack sx={{ marginTop: 'auto', paddingBottom: '24px' }}>
            {children}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
